import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";

import { css } from "@emotion/css";

import BG from "../../../img/jpg/contact/bg.jpg";
import BGDesktop from "../../../img/jpg/contact/bg-desktop.jpg";
import BGMobile from "../../../img/jpg/contact/bg-mobile.jpg";
import BGForm from "../../../img/jpg/contact/bg-form.jpg";
import DropdownIcon from "../../../img/svg/dropdown.svg";
import GenderIcon from "../../../img/svg/contact/gender.svg";
import MessageIcon from "../../../img/svg/contact/message.svg";
import EmailIcon from "../../../img/svg/contact/email.svg";
import SubmitButton from "../../../img/svg/contact/submit.svg";
import ErrorIcon from "../../../img/svg/error.svg";
import Clickable from "../../../components/Clickable";
import { desktop, laptop, tablet } from "../../../styles/breakpoints";
import { Social } from "../../../components/Footer";
import { useI18n } from "../../../i18n";

const Contact = ({ type }) => {
  const { t } = useI18n();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [active, setActive] = useState(false);
  const [gender, setGender] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    display: false,
    message: "",
    type: "",
  });

  const toggleAlert = (message, type) => {
    setAlertInfo({ display: true, message, type });
    setTimeout(() => {
      setAlertInfo({ display: false, message: "", type: "" });
    }, 5000);
  };

  const onSubmit = async (data) => {
    setDisabled(true);
    toggleAlert(t("contact_alert_sending"), "success");
    const { first_name, last_name, email, message, company_name } = data;
    try {
      const templateParams = {
        gender: gender,
        first_name: first_name,
        last_name: last_name,
        email: email,
        message: message,
        company_name: company_name,
      };

      await emailjs.send(
        process.env.REACT_APP_MAILJS_SERVICE_ID,
        type === "contact"
          ? process.env.REACT_APP_MAILJS_TEMPLATE_ID
          : process.env.REACT_APP_MAILJS_TEMPLATE_ID2,
        templateParams
      );

      toggleAlert(t("contact_alert_success"), "success");
      localStorage.setItem(type, true);
    } catch (e) {
      console.error("Form submission failed");
      toggleAlert(t("contact_alert_error"), "danger");
      setDisabled(false);
    } finally {
      reset();
    }
  };

  useEffect(() => emailjs.init(process.env.REACT_APP_MAILJS_KEY), []);

  return (
    <div className={style.container}>
      <span className="bg"></span>
      <div className="social-wrapper">
        <Social />
      </div>
      <form id="contact-form">
        <div className="wrapper">
          {type === "contact" && (
            <>
              <h2>
                {t("contact_wanna_ask")}
                <br />
                {t("contact_to_ask")}
              </h2>
              <h3>{t("contact_write_us")}</h3>
            </>
          )}
          {type === "book" && (
            <>
              <h3>{t("contact_level_up")}</h3>
              <h4>{t("contact_reply_24h")}</h4>
            </>
          )}
          {type === "subscribe" && (
            <>
              <h3>{t("contact_coming_soon")}</h3>
              <h4>{t("contact_first_to_know")}</h4>
            </>
          )}
          <div className="row nowrap">
            <div
              onClick={() => setActive(!active)}
              className={`select-wrapper ${active ? "active" : ""}`}
            >
              <label onClick={() => setActive(!active)}>{t("contact_gender")}</label>
              <Clickable className="value">
                {gender ? gender : t("contact_gender_placeholder")}
              </Clickable>
              <div className={`dropdown`}>
                <Clickable
                  onClick={() => {
                    setGender(t("contact_female"));
                    setActive(false);
                  }}
                >
                  <div>{t("contact_female")}</div>
                </Clickable>
                <Clickable
                  onClick={() => {
                    setGender(t("contact_male"));
                    setActive(false);
                  }}
                >
                  <div>{t("contact_male")}</div>
                </Clickable>
                <Clickable
                  onClick={() => {
                    setGender(t("contact_decline"));
                    setActive(false);
                  }}
                >
                  <div>{t("contact_decline")}</div>
                </Clickable>
              </div>
              {errors.title && (
                <span className="error">{errors.gender.message}</span>
              )}
            </div>

            <div className="input-wrapper">
              <label htmlFor="first_name">{t("contact_first_name")}</label>
              <input
                type="text"
                name="first_name"
                placeholder={t("contact_placeholder_name")}
                {...register("first_name", {
                  required: {
                    value: true,
                    message: t("contact_err_first"),
                  },
                  maxLength: {
                    value: 30,
                    message: t("contact_err_chars"),
                  },
                })}
              ></input>
              {errors.first_name && (
                <span className="error">{errors.first_name.message}</span>
              )}
            </div>

            <div className="input-wrapper">
              <label htmlFor="last_name">{t("contact_last_name")}</label>
              <input
                type="text"
                name="last_name"
                placeholder={t("contact_placeholder_name")}
                {...register("last_name", {
                  required: {
                    value: true,
                    message: t("contact_err_last"),
                  },
                  maxLength: {
                    value: 30,
                    message: t("contact_err_chars"),
                  },
                })}
              ></input>
              {errors.last_name && (
                <span className="error">{errors.last_name.message}</span>
              )}
            </div>
          </div>

          {type === "book" && (
            <div className="row">
              <div className="input-wrapper">
                <label htmlFor="company_name">{t("contact_company_name")}</label>
                <input
                  type="text"
                  name="company_name"
                  placeholder={t("contact_placeholder_company")}
                  {...register("company_name", {
                    required: {
                      value: true,
                      message: t("contact_err_company"),
                    },
                  })}
                ></input>
                {errors.company_name && (
                  <span className="error">{errors.company_name.message}</span>
                )}
              </div>
            </div>
          )}

          <div className="row">
            <div className="input-wrapper full email-input">
              <label htmlFor="email">{t("contact_email")}</label>
              <input
                type="email"
                name="email"
                {...register("email", {
                  required: true,
                  pattern:
                    /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                })}
                placeholder={t("contact_placeholder_email")}
              ></input>
              {errors.email && (
                <span className="error">
                  {t("contact_valid_email")}
                </span>
              )}
            </div>
          </div>
          {type === "contact" && (
            <div className="row">
              <div className="input-wrapper full message-input">
                <label htmlFor="message">{t("contact_message")}</label>
                <textarea
                  {...register("message", {
                    maxLength: {
                      value: 30,
                      message: t("contact_err_chars"),
                    },
                  })}
                  name="message"
                  cols="40"
                  rows="5"
                  placeholder={t("contact_placeholder_message")}
                ></textarea>
                {errors.message && (
                  <span className="error">{errors.message.message}</span>
                )}
              </div>
            </div>
          )}
          {alertInfo.display && (
            <div className={`alert ${alertInfo.type}`} role="alert">
              {alertInfo.message}
            </div>
          )}
          {!disabled && (
            <Clickable
              className="submit"
              onClick={handleSubmit((d) => onSubmit(d))}
            >
              <img src={SubmitButton} alt={t("contact_submit")} />
            </Clickable>
          )}
        </div>
      </form>
    </div>
  );
};

const style = {
  container: css({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignContent: "stretch",
    justifyContent: "flex-end",
    alignItems: "stretch",
    height: 1024,

    ".alert": {
      fontFamily: "Syne",
      fontSize: 22,
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "normal",
      //position: "absolute",
      marginTop: 20,
      marginBottom: 20,
      bottom: 130,
      right: 0,

      "&.success": {
        color: "#A8EF12",
      },
      "&.danger": {
        color: "#ff4c4c",
      },
    },

    "h2, h3": {
      color: "#fff",
      textAlign: "center",
      fontFamily: "Syne",
      fontSize: 76,
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "70px",
      opacity: 0.4,
    },
    h3: {
      opacity: 1,
      marginBottom: 60,
    },
    h4: {
      color: "#fff",
      textAlign: "center",
      fontFamily: "Syne",
      fontSize: 30,
      fontStyle: "normal",
      lineHeight: "40px",
      opacity: 0.4,
      marginTop: -40,
      marginBottom: 60,
    },
    label: {
      position: "absolute",
      background: "#000",
      left: 14,
      top: "-10px",
      color: "#FFF",
      fontFamily: "Inter",
      fontSize: 14.52,
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "normal",
      padding: 3,
      zIndex: 1,
    },
    ".social-wrapper": {
      position: "absolute",
      bottom: -50,
      left: "20%",
    },
    ".bg": {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundImage: `url(${BG})`,
      zIndex: 0,
    },
    form: {
      backgroundRepeat: "no-repeat",
      backgroundPosition: "20% 100%",
      backgroundSize: "cover",
      backgroundImage: `url(${BGForm})`,
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap",
      alignContent: "center",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#000",
      width: "50%",
      paddingLeft: 40,
      paddingRight: 40,
      paddingBottom: 50,
      paddingTop: 50,
      borderRadius: "100px 0 0 100px",
      zIndex: 1,
      position: "relative",

      ".wrapper": {
        maxWidth: 650,
        position: "relative",
      },
    },
    ".nowrap": {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gridTemplateRows: "1fr",
      justifyItems: "stretch",
      gap: 20,

      ".select-wrapper, .input-wrapper": {
        backgroundColor: "black",
        width: "32% !important",
      },
    },
    ".row": {
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      alignContent: "center",
      gap: 12,
      marginBottom: 20,
      width: "100%",
      flexWrap: "nowrap",

      ".select-wrapper": {
        position: "relative",
        border: "1.21px solid rgba(255, 255, 255, 0.20)",
        padding: "20px 17px",
        borderRadius: 20,
        width: "100%",

        ".value": {
          color: "#FFF",
          fontFamily: "Inter",
          fontSize: 16.94,
          fontStyle: "normal",
          lineHeight: "normal",
          letterSpacing: 0.339,
          marginLeft: 40,
          fontWeight: 200,
          opacity: 0.6,
          width: "100%",
        },

        "&.active": {
          border: "2px solid #6201FF",
          ".value": {
            fontWeight: 400,
            opacity: 0.6,
          },
          ".dropdown": {
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            alignContent: "center",
            justifyContent: "center",
            alignItems: "stretch",
          },
        },
        ".dropdown": {
          position: "absolute",
          display: "none",
          width: "100%",
          flexDirection: "column",
          flexWrap: "nowrap",
          alignContent: "center",
          justifyContent: "center",
          alignItems: "stretch",
          borderRadius: 20,
          border: "2px solid #6201FF",
          boxShadow: "0px 2.42px 2.42px 0px rgba(255, 255, 255, 0.05)",
          top: 70,
          zIndex: 2,
          left: 0,
          overflow: "hidden",
          flexGrow: 1,

          span: {
            position: "relative",
            background: "#000",
            flexGrow: 1,

            div: {
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 16.94,
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              letterSpacing: 0.339,
              borderBottom: "1px solid rgba(255, 255, 255, 0.20)",
              paddingTop: 24,
              paddingBottom: 24,
              marginLeft: 20,
              marginRight: 20,
              display: "block",
              zIndex: 999,
              position: "relative",
              flexGrow: 1,
            },

            "&:hover": {
              background: "#6201FF",
            },
          },
        },
        "&:after": {
          content: '""',
          width: 22,
          height: 22,
          background: `url("${DropdownIcon}") 0 0 no-repeat`,
          backgroundSize: "cover",
          top: 20,
          right: 15,
          position: "absolute",
          display: "block",
          zIndex: 1,
        },
        "&:before": {
          content: '""',
          width: 26.62,
          height: 26.62,
          background: `url("${GenderIcon}") 0 0 no-repeat`,
          backgroundSize: "cover",
          top: 17,
          left: 15,
          position: "absolute",
          display: "block",
          zIndex: 1,
        },
      },
      ".input-wrapper, .select-wrapper": {
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "stretch",
        position: "relative",
        borderRadius: 20,
      },

      ".error": {
        position: "relative",
        color: "#FF652B",
        fontFamily: "Inter",
        fontSize: 13.31,
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "normal",
        marginTop: 7,
        marginBottom: 10,
        borderRadius: 7.26,
        background: "#FAE9E5",
        padding: 7,
        paddingLeft: 30,

        "&:before": {
          content: '""',
          width: 19.36,
          height: 19.36,
          background: `url("${ErrorIcon}") 0 0 no-repeat`,
          backgroundSize: "cover",
          top: 0,
          bottom: 0,
          margin: "auto",
          left: 5,
          position: "absolute",
          display: "block",
          zIndex: 1,
        },
      },

      "input, select, textarea": {
        color: "#989898",
        fontFamily: "Inter",
        fontSize: 16.94,
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        letterSpacing: 0.339,
        borderRadius: 20,
        border: "1.21px solid rgba(255, 255, 255, 0.20)",
        padding: "20px 17px",
        background: "#000 !important",

        "&:focus, &:active, &:-webkit-autofill,&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
          {
            outlineStyle: "none",
            boxShadow: "none",
            border: "1.21px solid rgba(255, 255, 255, 0.20)",
            color: "white",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "#989898 !important",
            transition: "background-color 5000s ease-in-out 0s",
          },
        "&:focus": {
          border: "2px solid #6201FF",
          boxShadow: "0px 2px 2px 0px rgba(255, 255, 255, 0.05)",
        },
      },
      textarea: {
        paddingLeft: 45,
        paddingRight: 25,
        height: 25,
        overflow: "hidden",
        transition: "all 0.5s",

        "&:focus": {
          height: 150,
          overflowY: "scroll",
          marginBottom: -80,
        },
        "::-webkit-scrollbar": {
          width: 4,
        },

        "::-webkit-scrollbar-track": {
          boxShadow: "inset 0 0 5px grey",
          borderRadius: 10,
        },

        "::-webkit-scrollbar-thumb": {
          background: "#6201FF",
          borderRadius: 10,
        },
      },
    },
    ".message-input": {
      "&:before": {
        content: '""',
        width: 26.62,
        height: 26.62,
        background: `url("${MessageIcon}") 0 0 no-repeat`,
        backgroundSize: "cover",
        top: 17,
        left: 15,
        position: "absolute",
        display: "block",
        zIndex: 1,
      },
      input: {
        paddingLeft: 50,
      },
    },
    ".email-input": {
      "&:before": {
        content: '""',
        width: 21.78,
        height: 21.78,
        background: `url("${EmailIcon}") 0 0 no-repeat`,
        backgroundSize: "cover",
        top: 20,
        right: 15,
        position: "absolute",
        display: "block",
        zIndex: 1,
        opacity: 0.6,
      },
      input: {
        paddingRight: 40,
      },
    },

    ".full": {
      flex: "100%",
    },

    ".submit": {
      width: 400,
      height: 100,
      marginTop: 120,
      marginLeft: "auto",
      display: "block",

      img: {
        width: "100%",
        height: "100%",
      },
    },
    [desktop]: {
      flexDirection: "column-reverse",
      height: "auto",

      ".alert": {
        fontSize: 18,
        bottom: 120,
        left: 0,
        right: 0,
        margin: "auto",
        textAlign: "center",
      },
      ".social-wrapper": {
        bottom: 250,
        left: 0,
        right: 0,
        zIndex: 2,
      },
      ".bg": {
        position: "relative",
        left: 0,
        top: 0,
        width: "100%",
        height: 400,
        backgroundImage: `url(${BGDesktop})`,
        backgroundPosition: "30% 59%",
        zIndex: 0,
      },
      form: {
        height: 1024,
        width: "auto",
        borderRadius: 0,

        ".submit": {
          marginRight: "auto",
        },
      },
    },
    [laptop]: {
      ".alert": {
        fontSize: 16,
        bottom: 100,
      },
      ".social-wrapper": {
        bottom: 160,
      },
      "h2, h3": {
        fontSize: 63,
        fontStyle: "normal",
        fontWeight: 700,
        lineHeight: "58px",
      },
      h3: {
        marginBottom: 50,
      },
      h4: {
        fontSize: 26,
        lineHeight: "30px",
        marginBottom: 40,
      },
      label: {
        left: 14,
        top: "-10px",
        fontSize: 12,
      },
      ".bg": {
        position: "relative",
        left: 0,
        top: 0,
        width: "100%",
        height: 257,
        backgroundImage: `url(${BGDesktop})`,
        backgroundPosition: "30% 59%",
        zIndex: 0,
      },
      form: {
        height: 980,
        backgroundPosition: "50%",
        backgroundColor: "#000",
        padding: 20,
        borderRadius: 0,
        zIndex: 1,

        ".wrapper": {
          maxWidth: 540,
        },
        ".row": {
          gap: 12,
          marginBottom: 20,
          width: "100%",

          ".select-wrapper": {
            padding: 14.66,
            alignItems: "center",
            ".value": {
              fontSize: 14,
              marginLeft: 45,
              marginTop: 3,
            },
            ".dropdown": {
              span: {
                div: {
                  fontSize: 14,
                },
              },
            },
            "&:after": {
              width: 22,
              height: 22,
              top: 16,
              right: 15,
            },
            "&:before": {
              width: 22.15,
              height: 22.15,
              top: 15,
              left: 13,
            },
          },

          "input, select": {
            fontSize: 14,
            letterSpacing: 0.28,
            padding: "16px 14px",
            borderRadius: 17,
          },
          textarea: {
            paddingLeft: 45,
            paddingRight: 25,
            height: 16,
            fontSize: 14,
            paddingTop: 16,

            "&:focus": {
              height: 120,
              overflowY: "scroll",
              marginBottom: 0,
            },
          },
        },
        ".message-input": {
          "&:before": {
            width: 22.15,
            height: 22.15,
            top: 14,
            left: 15,
          },
          input: {
            paddingLeft: 40,
          },
        },
        ".email-input": {
          "&:before": {
            width: 18.12,
            height: 18.12,
            top: 17,
            right: 15,
          },
          input: {
            paddingRight: 30,
          },
        },
      },
      ".submit": {
        width: 350,
        height: 80,
        marginTop: 60,
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
      },
    },
    [tablet]: {
      ".alert": {
        fontSize: 14,
        bottom: 75,
      },
      ".social-wrapper": {
        bottom: 190,
      },
      "h2, h3": {
        fontSize: 38,
        fontStyle: "normal",
        fontWeight: 700,
        lineHeight: "42px",
      },

      h4: {
        fontSize: 20,
        lineHeight: "24px",
        marginBottom: 30,
      },
      h3: {
        marginBottom: 35,
      },
      label: {
        left: 14,
        top: "-10px",
        fontSize: 12,
      },
      ".bg": {
        position: "relative",
        left: 0,
        top: 0,
        width: "100%",
        height: 257,
        backgroundImage: `url(${BGMobile})`,
        backgroundPosition: "30% 59%",
        zIndex: 0,
      },
      form: {
        height: "auto",
        padding: 20,
        paddingBottom: 80,
        paddingTop: 80,

        ".wrapper": {
          width: "100%",
        },
        ".nowrap": {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignContent: "center",
          justifyContent: "center",
          alignItems: "center",
          gap: 20,

          ".select-wrapper, .input-wrapper": {
            width: "50%",
            "&:nth-child(3)": {
              width: "100%",
            },
          },
        },
        ".row": {
          gap: 12,
          marginBottom: 20,
          width: "100%",

          ".select-wrapper": {
            flex: "100%",
            marginBottom: 5,

            ".value": {
              marginTop: 3,
              marginLeft: 50,
            },
          },
        },
      },
      ".submit": {
        width: 250,
        height: 60,
        marginTop: 46,
      },
    },
  }),
};

export default Contact;
